/**
 * OrdersHistoryCard.tsx — the patient's OWN order-history card (PATIENT
 * surfaces only).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: order-history resume
 * follow-up (CTO note on PR #134, 2026-07-30).
 *
 * WHY: the dashboard landing screen and the My orders section both show the
 * patient's order history, and until this extraction each kept its OWN copy of
 * the card — twice the copies drifted (the Continue-checkout affordance landed
 * tested in one and untested in the other; the double-h1 fix hit the same
 * duplicated card). ONE component, consumed by both, so a row change cannot be
 * added to one surface and forgotten on the other. Row rules: patient-facing
 * status copy (never the SCREAMING wire enums), true-UTC dates, the shared
 * `resumeHref` drop-off rule (a resumable row without an assessmentRef renders
 * plain — no dead button), and the tracking line renders nothing until the
 * pharmacy has dispatched.
 */
import { Link } from 'react-router-dom';
import type { OrderSummary } from '../api/types';
import { formatUtcDate } from '../lib/format';
import { resumeHref } from '../lib/orderResume';
import { displayTreatmentName, orderStatusCopy } from '../lib/patientStatusCopy';
import { ButtonArrow, buttonClass, buttonStyle, Card, Help } from './kit';
import { IconPipeline } from './icons';
import { OrderTrackingLine } from './OrderTrackingLine';
import { PatientStatusBadge } from './PatientStatusBadge';
import { CardIconHeader } from './sections';

export function OrdersHistoryCard({ orders }: { orders: OrderSummary[] }) {
  return (
    <Card className="gps-card-interactive">
      <CardIconHeader icon={<IconPipeline />} title="Order history" />
      {orders.length === 0 ? (
        <Help style={{ marginBottom: 0 }}>No orders yet.</Help>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {orders.map((order) => {
            const resume = resumeHref(order);
            return (
              <li key={order.orderCaseId} className="gps-data-row">
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 'var(--gps-s-2)', alignItems: 'center' }}>
                  <span style={{ color: 'var(--gps-text)', fontWeight: 600 }}>{displayTreatmentName(order.medicationName, order.productKey)}</span>
                  <PatientStatusBadge status={orderStatusCopy(order.state)} />
                </div>
                <div style={{ fontSize: '13px', color: 'var(--gps-muted)', marginTop: '2px' }}>
                  {formatUtcDate(order.createdAt)}
                </div>
                {resume ? (
                  <div style={{ marginTop: 'var(--gps-s-3)', maxWidth: '16rem' }}>
                    <Link to={resume} className={buttonClass('cta')} style={buttonStyle('cta')}>
                      Continue checkout
                      <ButtonArrow />
                    </Link>
                  </div>
                ) : null}
                {/* Courier + tracking once the pharmacy has dispatched — renders
                    nothing until then (pharmacy dispensing lane, 2026-07-29). */}
                <OrderTrackingLine tracking={order.tracking} />
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
